import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';

const SmoothScroll = () => {
  const location = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    // Initialize Lenis
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      mouseMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    lenisRef.current = lenis;

    let rafId;

    // RAF loop
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }

    rafId = requestAnimationFrame(raf);

    // Initial scroll check (for reload or direct visited URL)
    if (location.hash) {
      // Small timeout to ensure DOM is ready (especially with lazy loading wrappers)
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          lenis.scrollTo(element, { offset: 0, immediate: true });
        }
      }, 100);
    }

    // Handle anchor links click for same-page scroll (including same-hash clicks)
    const handleAnchorClick = (e) => {
      const target = e.target.closest('a');
      if (!target) return;
      
      const href = target.getAttribute('href');
      if (!href) return;

      // Check if it's a hash link
      if (href.startsWith('#') || (href.startsWith('/') && href.includes('#'))) {
        const [path, hash] = href.split('#');
        const currentPath = window.location.pathname;

        // Only handle if it's the same page or just a hash
        if (href.startsWith('#') || path === currentPath || path === '') {
           const targetId = '#' + hash;
           const element = document.querySelector(targetId);
           
           if (element) {
             // For Link components, we don't prevent default to let Router update URL
             // But we force scroll
             lenis.scrollTo(element, { offset: 0 });
           }
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);

    // Clean up
    return () => {
      document.removeEventListener('click', handleAnchorClick);
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount for Lenis init

  // Effect to handle location changes (Router navigation)
  useEffect(() => {
    if (location.hash && lenisRef.current) {
      // Wait 600ms for all lazy sections / images to finish expanding their heights
      // before calculating scroll target position once. This avoids animation interruptions
      // caused by page layout shifting mid-scroll.
      const timeoutId = setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          lenisRef.current.scrollTo(element, { offset: 0 });
        }
      }, 600);

      return () => clearTimeout(timeoutId);
    } else if (lenisRef.current) {
      // Scroll to top on route change if no hash
      lenisRef.current.scrollTo(0, { immediate: true });
    }
  }, [location]); // Trigger whenever location (path or hash) changes

  return null;
};

export default SmoothScroll;
